import React from 'react';
import { useDispatch } from 'react-redux';
import api, { useClearExtensionsMutation, useClearWorkspaceCacheMutation } from '../store/Api';
import { useClearSearchMutation } from '../store/endpoints/SavedSearchEndpoints';
import { useSetSelectedOrganizationMutation } from '../store/endpoints/OrganizationsEndpoints';
import {
    useClearBundleCacheMutation,
    useClearBundleRefsCacheMutation,
} from '../store/endpoints/ProductBundleEndpoints';
import { clearSettings } from '../store/settingsSlice';

/**
 * Switch the selected organization and clear everything scoped to the old one.
 *
 * Extracted from the nav menu's organization switcher so the settings deep-link resolver performs
 * exactly the same switch - every cached query carries the previous tenant's `x-organization`, so
 * missing one of these leaves stale cross-tenant data on screen.
 */
export const useSwitchOrganization = (): ((organization: string) => Promise<void>) => {
    const dispatch = useDispatch();
    const [setOrganization] = useSetSelectedOrganizationMutation();
    const [clearWorkspaces] = useClearWorkspaceCacheMutation();
    const [clearSearch] = useClearSearchMutation();
    const [clearExtensions] = useClearExtensionsMutation();
    const [clearBundleRefs] = useClearBundleRefsCacheMutation();
    const [clearBundles] = useClearBundleCacheMutation();

    return React.useCallback(
        async (organization: string) => {
            await setOrganization(organization);
            dispatch(clearSettings());
            clearSearch();
            clearWorkspaces();
            clearExtensions();
            clearBundles();
            clearBundleRefs();
            dispatch(api.util.resetApiState());
        },
        [dispatch, setOrganization, clearSearch, clearWorkspaces, clearExtensions, clearBundles, clearBundleRefs]
    );
};

export default useSwitchOrganization;
